/**
 * /slides/:id/present — 발표(presentation) 모드.
 *
 * 별도 layout: 사이드바 없음, 헤더 없음 (호버 시에만 작은 컨트롤 노출).
 * 단일 페이지 풀스크린, 키보드 네비, speaker notes 토글, 풀스크린 토글.
 *
 * 키보드:
 *   →/Space/PageDown/n  : 다음
 *   ←/PageUp/p          : 이전
 *   Home                : 첫
 *   End                 : 마지막
 *   Esc                 : 종료 (편집기로 복귀)
 *   f                   : 풀스크린 토글
 *   s                   : speaker notes 토글
 *   숫자 + Enter        : 해당 페이지로 점프
 */
import { PageRenderer } from '@/components/slides/PageRenderer';
import { SlideCanvas } from '@/components/slides/SlideCanvas';
import { api } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Maximize2, Minimize2, StickyNote, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

interface Props {
  slideId: string;
}

export function SlidesPresent({ slideId }: Props) {
  const navigate = useNavigate();
  const [pageIndex, setPageIndex] = useState(0);
  const [showNotes, setShowNotes] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showHeader, setShowHeader] = useState(false);
  const [pageJumpBuffer, setPageJumpBuffer] = useState('');
  const headerHideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const slideQuery = useQuery({
    queryKey: ['slide', slideId],
    queryFn: () => api.slideGet(slideId),
    staleTime: 5_000,
  });
  const assetsQuery = useQuery({
    queryKey: ['slide-assets', slideId],
    queryFn: () => api.slideAssets(slideId),
    staleTime: 5_000,
  });

  const pages = slideQuery.data?.pages ?? [];
  const total = pages.length;
  const currentPage = pages[pageIndex] ?? null;

  const goNext = useCallback(() => {
    setPageIndex((i) => Math.min(i + 1, total - 1));
  }, [total]);
  const goPrev = useCallback(() => {
    setPageIndex((i) => Math.max(i - 1, 0));
  }, []);
  const exit = useCallback(() => {
    navigate({ to: '/slides/$slideId', params: { slideId } });
  }, [navigate, slideId]);

  const toggleFullscreen = useCallback(async () => {
    try {
      if (!document.fullscreenElement) {
        await containerRef.current?.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch {
      // 일부 브라우저는 사용자 제스처가 끊기면 reject. 무시.
    }
  }, []);

  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  // 키보드 네비 — 단순 페이지 점프 버퍼 포함.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      switch (e.key) {
        case 'ArrowRight':
        case ' ':
        case 'PageDown':
        case 'n':
          e.preventDefault();
          goNext();
          break;
        case 'ArrowLeft':
        case 'PageUp':
        case 'p':
          e.preventDefault();
          goPrev();
          break;
        case 'Home':
          e.preventDefault();
          setPageIndex(0);
          break;
        case 'End':
          e.preventDefault();
          setPageIndex(Math.max(0, total - 1));
          break;
        case 'Escape':
          e.preventDefault();
          if (document.fullscreenElement) {
            void document.exitFullscreen();
          } else {
            exit();
          }
          break;
        case 'f':
        case 'F':
          e.preventDefault();
          void toggleFullscreen();
          break;
        case 's':
        case 'S':
          e.preventDefault();
          setShowNotes((v) => !v);
          break;
        case 'Enter':
          if (pageJumpBuffer.length > 0) {
            const target = Number.parseInt(pageJumpBuffer, 10);
            if (Number.isFinite(target) && target >= 1 && target <= total) {
              setPageIndex(target - 1);
            }
            setPageJumpBuffer('');
          }
          break;
        default:
          if (/^[0-9]$/.test(e.key)) {
            setPageJumpBuffer((b) => (b + e.key).slice(0, 3));
            return;
          }
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goNext, goPrev, exit, toggleFullscreen, total, pageJumpBuffer]);

  // 마우스 이동 시 헤더 잠시 표시.
  const onMouseMove = useCallback(() => {
    setShowHeader(true);
    if (headerHideTimerRef.current) clearTimeout(headerHideTimerRef.current);
    headerHideTimerRef.current = setTimeout(() => setShowHeader(false), 2500);
  }, []);
  useEffect(() => {
    return () => {
      if (headerHideTimerRef.current) clearTimeout(headerHideTimerRef.current);
    };
  }, []);

  if (slideQuery.isLoading) {
    return (
      <div className="h-[100dvh] grid place-items-center bg-black text-white text-sm">
        슬라이드 불러오는 중…
      </div>
    );
  }
  if (slideQuery.isError || !slideQuery.data) {
    return (
      <div className="h-[100dvh] grid place-items-center bg-black text-destructive text-sm">
        슬라이드를 찾을 수 없습니다.
      </div>
    );
  }
  if (total === 0 || !currentPage) {
    return (
      <div className="h-[100dvh] grid place-items-center bg-black text-white text-sm flex-col gap-3">
        <div>아직 페이지가 없어요.</div>
        <button
          type="button"
          onClick={exit}
          className="px-3 py-1.5 rounded-md bg-white/10 hover:bg-white/20 text-xs"
        >
          편집기로 돌아가기
        </button>
      </div>
    );
  }

  const slide = slideQuery.data;
  const notes = currentPage.notes ?? '';

  return (
    <div
      ref={containerRef}
      onMouseMove={onMouseMove}
      className="h-[100dvh] w-screen bg-black text-white overflow-hidden relative cursor-default"
    >
      {/* 풀스크린 캔버스 */}
      <div className="absolute inset-0">
        <SlideCanvas design={slide.design}>
          <PageRenderer
            page={currentPage}
            design={slide.design}
            assets={assetsQuery.data?.assets ?? []}
          />
        </SlideCanvas>
      </div>

      {/* 좌하단 — 페이지 인디케이터 */}
      <div
        className="absolute left-4 bottom-4 px-2.5 py-1 rounded-md bg-black/60 text-white text-xs tabular-nums backdrop-blur-sm"
        aria-live="polite"
      >
        {pageIndex + 1} / {total}
        {pageJumpBuffer && <span className="ml-2 text-yellow-300">→ {pageJumpBuffer} (Enter)</span>}
      </div>

      {/* 우상단 — 컨트롤 (호버 시 fade in) */}
      <div
        className={`absolute right-4 top-4 flex items-center gap-1 transition-opacity duration-300 ${
          showHeader ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        <ControlBtn label="speaker notes (S)" onClick={() => setShowNotes((v) => !v)}>
          <StickyNote className="size-4" />
        </ControlBtn>
        <ControlBtn label="풀스크린 (F)" onClick={toggleFullscreen}>
          {isFullscreen ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
        </ControlBtn>
        <ControlBtn label="종료 (Esc)" onClick={exit}>
          <X className="size-4" />
        </ControlBtn>
      </div>

      {/* speaker notes — 하단 패널 */}
      {showNotes && notes.trim().length > 0 && (
        <div className="absolute left-1/2 -translate-x-1/2 bottom-12 max-w-3xl w-[80%] max-h-[35vh] overflow-y-auto bg-black/80 text-white text-base p-4 rounded-lg backdrop-blur-sm border border-white/10">
          <div className="text-[10px] uppercase tracking-wide text-white/50 mb-1">
            speaker notes
          </div>
          <div className="whitespace-pre-wrap leading-relaxed">{notes}</div>
        </div>
      )}

      {/* 좌우 클릭 영역 (마우스 발표용) */}
      <button
        type="button"
        aria-label="이전 페이지"
        onClick={goPrev}
        disabled={pageIndex === 0}
        className="absolute inset-y-0 left-0 w-1/4 cursor-w-resize disabled:cursor-not-allowed bg-transparent"
        tabIndex={-1}
      />
      <button
        type="button"
        aria-label="다음 페이지"
        onClick={goNext}
        disabled={pageIndex >= total - 1}
        className="absolute inset-y-0 right-0 w-1/4 cursor-e-resize disabled:cursor-not-allowed bg-transparent"
        tabIndex={-1}
      />
    </div>
  );
}

function ControlBtn({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="size-9 grid place-items-center rounded-md bg-black/60 hover:bg-black/80 text-white backdrop-blur-sm"
    >
      {children}
    </button>
  );
}
